import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useSettingsResource } from './useSettingsResource.js'

/** The shape `/v1/shipping/settings` reads and writes. */
export interface ShippingMethodRow {
  id: string
  label: string
  hint: string
  mode: 'rate' | 'add' | 'fixed' | 'free'
  charge: number
  daysDelta: number
  enabled: boolean
}

export interface ShippingZoneRow {
  label: string
  match: string
  /** Null: priced by the rate rules. */
  rate: number | null
  extraDays: number
  cod: boolean
  blocked: boolean
}

export interface ShippingSettings {
  shippingMode: 'flat' | 'slabs' | 'weight'
  shippingFlat: number
  shippingSlabs: Array<{ upTo: number; rate: number }>
  shippingPerItem: number
  weightBaseGrams: number
  weightBaseRate: number
  weightStepGrams: number
  weightStepRate: number
  defaultGrams: number

  freeShipAbove: number
  freeShipPrepaidOnly: boolean
  codExtra: number

  methodsEnabled: boolean
  methods: ShippingMethodRow[]

  zonesEnabled: boolean
  zones: ShippingZoneRow[]
  blockedPincodes: string[]

  deliveryCutoff: string
  processingDays: number
  shipDays: number[]
  holidays: string[]
  deliveryDaysMin: number
  deliveryDaysMax: number

  onlineDiscountEnabled: boolean
  onlineDiscountType: 'percent' | 'fixed'
  onlineDiscountValue: number
  onlineDiscountMax: number
  codFee: number
  codPrepaidNudge: boolean
}

export interface ShippingPreview {
  place: { city: string | null; state: string | null }
  quote: {
    serviceable: boolean
    reason: string | null
    zone: string | null
    free: boolean
    options: Array<{ id: string; label: string; charge: number; eta: string }>
    estimate: { label: string; dispatch: string }
  }
  codAllowed: boolean
  totals: { shipping: number; codFee: number; onlineDiscount: number; orderTotal: number; prepaidTotal: number }
}

export function useShippingSettings() {
  return useSettingsResource<ShippingSettings>('/v1/shipping/settings', 'shipping-settings')
}

export function useShippingPreview() {
  return useMutation({
    mutationFn: (body: { pincode: string; subtotal: number; grams: number; paymentMethod: 'upi' | 'cod' }) =>
      api<ShippingPreview>('/v1/shipping/preview', { method: 'POST', body }),
  })
}
